import React__default, { useState, useRef, useMemo, useCallback, createElement, Fragment, useEffect } from 'react';
import { dz as generateArray, ai as createClassName, dA as randomInteger, u as useStrings, dt as getResponsiveValue, U as FontAwesomeIcon, c as classNames } from './className-0524835a.js';
import { b as Skeleton, u as useElementWidth } from './SkeletonContainer-fd90b3af.js';
import { I as IconButton, f as faTimesExports } from './Portal-0a1b6f69.js';
import { C as Checkbox } from './DateText-ac378e62.js';
import { b as Menu, c as MenuItem } from './MenuItem-0e2b904d.js';

var faCaretDown = {};

var hasRequiredFaCaretDown;

function requireFaCaretDown () {
	if (hasRequiredFaCaretDown) return faCaretDown;
	hasRequiredFaCaretDown = 1;
	(function (exports) {
		Object.defineProperty(exports, '__esModule', { value: true });
		var prefix = 'fas';
		var iconName = 'caret-down';
		var width = 320;
		var height = 512;
		var ligatures = [];
		var unicode = 'f0d7';
		var svgPathData = 'M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z';

		exports.definition = {
		  prefix: prefix,
		  iconName: iconName,
		  icon: [
		    width,
		    height,
		    ligatures,
		    unicode,
		    svgPathData
		  ]};

		exports.faCaretDown = exports.definition;
		exports.prefix = prefix;
		exports.iconName = iconName;
		exports.width = width;
		exports.height = height;
		exports.ligatures = ligatures;
		exports.unicode = unicode;
		exports.svgPathData = svgPathData;
} (faCaretDown));
	return faCaretDown;
}

var faCaretDownExports = requireFaCaretDown();

var faCaretUp = {};

var hasRequiredFaCaretUp;

function requireFaCaretUp () {
	if (hasRequiredFaCaretUp) return faCaretUp;
	hasRequiredFaCaretUp = 1;
	(function (exports) {
		Object.defineProperty(exports, '__esModule', { value: true });
		var prefix = 'fas';
		var iconName = 'caret-up';
		var width = 320;
		var height = 512;
		var ligatures = [];
		var unicode = 'f0d8';
		var svgPathData = 'M288.662 352H31.338c-17.818 0-26.741-21.543-14.142-34.142l128.662-128.662c7.81-7.81 20.474-7.81 28.284 0l128.662 128.662c12.6 12.599 3.676 34.142-14.142 34.142z';

		exports.definition = {
		  prefix: prefix,
		  iconName: iconName,
		  icon: [
		    width,
		    height,
		    ligatures,
		    unicode,
		    svgPathData
		  ]};

		exports.faCaretUp = exports.definition;
		exports.prefix = prefix;
		exports.iconName = iconName;
		exports.width = width;
		exports.height = height;
		exports.ligatures = ligatures;
		exports.unicode = unicode;
		exports.svgPathData = svgPathData;
} (faCaretUp));
	return faCaretUp;
}

var faCaretUpExports = requireFaCaretUp();

const TableRowSkeleton = ({ isCompactTable, hasSelection, columnCount, actionCount = 0 }) => {
    return React__default.createElement("tr", null,
        hasSelection && React__default.createElement("td", { className: className('selection') },
            React__default.createElement(Skeleton, { variant: "rounded", width: 18, height: 18 })),
        isCompactTable
            ? React__default.createElement("td", null,
                React__default.createElement("div", { className: className('compact') },
                    React__default.createElement("div", null, generateArray(columnCount, index => React__default.createElement(Skeleton, { key: index, width: randomWidth() }))),
                    actionCount > 0 && React__default.createElement("div", { className: className('actions') }, generateArray(actionCount, index => React__default.createElement(Skeleton, { key: index, variant: "circular", height: 22, width: 22 })))))
            : React__default.createElement(React__default.Fragment, null,
                generateArray(columnCount, index => React__default.createElement("td", { key: index, className: className('cell') },
                    React__default.createElement(Skeleton, { width: randomWidth() }))),
                actionCount > 0 && React__default.createElement("td", null,
                    React__default.createElement("div", { className: className('actions') }, generateArray(actionCount, index => React__default.createElement(Skeleton, { key: index, variant: "circular", height: 16, width: 16 }))))));
};
const className = createClassName('TableRowSkeleton');
const randomWidth = () => `${randomInteger(30, 70)}%`;

function Table(props) {
    var _a, _b;
    const { table: strings } = useStrings();
    const { ref, width } = useElementWidth();
    const [focusedRowIndex, setFocusedRowIndex] = useState(-1);
    const sortMenuAnchor = useRef(null);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
    const { columns, rowCount, selectedIds, isLoading, getRow, getRowFallback, onClick, onKeyPress, onSelectionChange } = props;
    const { sortKey, sortDirection, canClear, onSort } = (_a = props.sort) !== null && _a !== void 0 ? _a : {};
    const selection = (_b = props.selection) !== null && _b !== void 0 ? _b : 'none';
    const compactHeader = getResponsiveValue(props.compactHeader, width);
    const rows = useMemo(() => {
        const rowObjects = [];
        for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
            rowObjects.push(getRow(rowIndex));
        }
        return rowObjects;
    }, [rowCount, getRow]);
    const selectionHelper = useMemo(() => {
        const rowIds = rows.map(row => row.id);
        function getSelectedIds() {
            var _a;
            switch (selection) {
                case 'none':
                    return [];
                case 'single':
                    return (_a = selectedIds === null || selectedIds === void 0 ? void 0 : selectedIds.slice(0, 1)) !== null && _a !== void 0 ? _a : [];
                case 'multi':
                    return selectedIds !== null && selectedIds !== void 0 ? selectedIds : [];
            }
        }
        function handleSelectionChange(ids) {
            if (ids.length === 0) {
                setFocusedRowIndex(-1);
            }
            onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(ids);
        }
        return new SelectionHelper(rowIds, getSelectedIds(), handleSelectionChange);
    }, [rows, selection, selectedIds, onSelectionChange]);
    const onHeaderClick = useCallback((column) => {
        if (compactHeader) {
            setIsSortMenuOpen(false);
        }
        if (column.sortKey !== undefined) {
            // click on sort by other column, default to ascending
            if (sortKey !== column.sortKey) {
                return onSort === null || onSort === void 0 ? void 0 : onSort(column.sortKey, 'ascending');
            }
            if (sortDirection === 'ascending') {
                return onSort === null || onSort === void 0 ? void 0 : onSort(column.sortKey, 'descending');
            }
            if (sortDirection === 'descending' && canClear) {
                return onSort === null || onSort === void 0 ? void 0 : onSort(column.sortKey, 'none');
            }
            if (sortDirection === 'descending' && !canClear) {
                return onSort === null || onSort === void 0 ? void 0 : onSort(column.sortKey, 'ascending');
            }
            if (sortDirection === 'none') {
                return onSort === null || onSort === void 0 ? void 0 : onSort(column.sortKey, 'ascending');
            }
        }
    }, [compactHeader, sortKey, sortDirection, canClear, onSort]);
    const headerElements = useMemo(() => {
        function onHeaderTick(event) {
            event.stopPropagation();
            if (event.target.checked || event.target.dataset.indeterminate === 'true') {
                selectionHelper.selectAll().fireChange();
            }
            else {
                selectionHelper.selectNone().fireChange();
            }
        }
        function getHeaderClass(column) {
            return classNames('Table-columnHeader', column.className, {
                'Table-sortableHeader': column.sortKey !== undefined,
                'Table-sortedHeader': sortKey === column.sortKey
            });
        }
        const elements = [];
        if (selection === 'multi') {
            const selectionState = selectionHelper.getState();
            const title = selectionState === 'all' ? strings.selectNone : strings.selectAll;
            elements.push(React__default.createElement("th", { className: "Table-selectCell", key: -1 },
                React__default.createElement(Checkbox, { color: "primary", disabled: rowCount === 0, checked: selectionState !== 'none', indeterminate: selectionState === 'some', title: title, inputProps: {
                        'aria-label': title
                    }, onChange: onHeaderTick })));
        }
        const sortArrow = (sortDirection !== 'none' || !canClear) &&
            React__default.createElement(FontAwesomeIcon, { className: "Table-sortIcon", icon: sortDirection === 'descending' ? faCaretDownExports.faCaretDown : faCaretUpExports.faCaretUp, size: "lg" });
        const showHeaderwithClearSortButton = props.showTableClearSortButton && sortDirection !== 'none';
        const clearSort = () => sortKey && (onSort === null || onSort === void 0 ? void 0 : onSort(sortKey, 'none'));
        const clearSortButton = (size) => showHeaderwithClearSortButton && (React__default.createElement(IconButton, { icon: faTimesExports.faTimes, iconSize: size, title: strings.clearSort, onClick: clearSort }));
        if (compactHeader) {
            const sortColumn = columns.find(column => column.sortKey === sortKey);
            if (sortKey !== undefined && sortColumn) {
                elements.push(React__default.createElement("th", { key: 0, className: "Table-columnHeader Table-sortableHeader Table-collapsedHeader", ref: sortMenuAnchor, onClick: () => setIsSortMenuOpen(true) },
                    React__default.createElement("div", { className: "Table-flexSpaceBetweenWrapper" },
                        React__default.createElement("div", { className: "Table-flexSpaceBetweenWrapper" },
                            React__default.createElement("span", { className: "Table-columnHeaderText" }, sortColumn.title),
                            "\u00A0",
                            sortArrow),
                        clearSortButton())));
            }
        }
        else {
            elements.push(...columns.map(column => {
                const isSortApplied = column.sortKey !== undefined && sortKey === column.sortKey;
                return (React__default.createElement("th", { title: column.title, key: column.id, className: getHeaderClass(column), onClick: () => onHeaderClick(column) },
                    React__default.createElement("div", { className: "Table-flexSpaceBetweenWrapper" },
                        React__default.createElement("div", null,
                            React__default.createElement("span", { className: "Table-columnHeaderText" }, column.title),
                            isSortApplied && sortArrow),
                        isSortApplied && clearSortButton('lg'))));
            }));
        }
        return elements;
    }, [selection, sortDirection, canClear, props.showTableClearSortButton, compactHeader, selectionHelper, sortKey, strings, rowCount, onSort, columns, onHeaderClick]);
    const rowElements = useMemo(() => {
        function onRowTick(rowIndex, event) {
            if (!event.shiftKey && !event.altKey) {
                event.stopPropagation();
                selectionHelper.toggle(rows[rowIndex].id).fireChange();
                setFocusedRowIndex(rowIndex);
            }
        }
        function onRowKeyDown(rowIndex, event) {
            if (event.shiftKey) {
                document.onselectstart = () => false;
            }
            // Cursor up and down.
            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault();
                const nextRowIndex = event.key === 'ArrowUp' ?
                    Math.max(0, focusedRowIndex - 1) :
                    Math.min(rows.length - 1, focusedRowIndex + 1);
                if (nextRowIndex !== focusedRowIndex) {
                    const rowId = rows[nextRowIndex].id;
                    if (selection === 'multi' && event.shiftKey) {
                        if (focusedRowIndex === -1) {
                            selectionHelper.selectOnly(rowId).fireChange();
                        }
                        else {
                            selectionHelper.toggleRange(focusedRowIndex, nextRowIndex).select(rowId).fireChange();
                        }
                    }
                    else {
                        selectionHelper.selectOnly(rowId).fireChange();
                    }
                    setFocusedRowIndex(nextRowIndex);
                }
            }
            else if (onKeyPress === null || onKeyPress === void 0 ? void 0 : onKeyPress(rowIndex, event.key)) {
                event.preventDefault();
            }
        }
        function onRowKeyUp() {
            document.onselectstart = null;
        }
        function onRowClick(rowIndex, event) {
            const rowId = rows[rowIndex].id;
            if (selection === 'single') {
                selectionHelper.selectOnly(rowId).fireChange();
                if (!event.shiftKey && !event.altKey) {
                    onClick === null || onClick === void 0 ? void 0 : onClick(rowIndex);
                }
            }
            else if (selection === 'multi') {
                if (event.shiftKey) {
                    if (focusedRowIndex === -1) {
                        selectionHelper.selectOnly(rowId).fireChange();
                    }
                    else {
                        selectionHelper.toggleRange(focusedRowIndex, rowIndex).fireChange();
                    }
                }
                else if (event.altKey) {
                    selectionHelper.toggle(rowId).fireChange();
                }
                else {
                    selectionHelper.selectOnly(rowId).fireChange();
                    onClick === null || onClick === void 0 ? void 0 : onClick(rowIndex);
                }
            }
            setFocusedRowIndex(rowIndex);
        }
        return rows.map((row, rowIndex) => {
            const classes = classNames({
                'Table-selectedRow': selectionHelper.isSelected(row.id)
            });
            const rowProps = {
                id: row.id,
                className: classes,
                tabIndex: selection !== 'none' ? 0 : undefined,
                isFocused: selection !== 'none' && focusedRowIndex === rowIndex,
                isSelected: selectionHelper.isSelected(row.id),
                showTick: selection === 'multi',
                onClick: event => selection !== 'none' ? onRowClick(rowIndex, event) : onClick === null || onClick === void 0 ? void 0 : onClick(rowIndex),
                onKeyDown: selection !== 'none' ? event => onRowKeyDown(rowIndex, event) : undefined,
                onKeyUp: selection !== 'none' ? onRowKeyUp : undefined,
                onTick: event => onRowTick(rowIndex, event)
            };
            const componentProps = Object.assign({ rowProps, isCompactTable: compactHeader }, row.props);
            return createElement(row.component, Object.assign(Object.assign({}, componentProps), { key: row.id }));
        });
    }, [focusedRowIndex, rows, selection, selectionHelper, compactHeader, onClick, onKeyPress]);
    const classes = classNames('Table', props.className, {
        'Table-clickable': props.onClick !== undefined || selection !== 'none',
        'Table-selectable': selection !== 'none'
    });
    const fallback = useMemo(() => {
        if (isLoading) {
            const rowCount = rows.length > 0 ? rows.length : 3;
            return generateArray(rowCount, index => {
                var _a;
                return React__default.createElement(Fragment, { key: index }, (_a = getRowFallback === null || getRowFallback === void 0 ? void 0 : getRowFallback(compactHeader !== null && compactHeader !== void 0 ? compactHeader : false)) !== null && _a !== void 0 ? _a : React__default.createElement(TableRowSkeleton, { isCompactTable: compactHeader, hasSelection: selection !== 'none', columnCount: columns.length }));
            });
        }
    }, [isLoading, rows.length, getRowFallback, compactHeader, selection, columns.length]);
    return React__default.createElement(React__default.Fragment, null,
        React__default.createElement("table", { ref: ref, className: classes, "aria-label": props.title },
            React__default.createElement("thead", null,
                React__default.createElement("tr", null, headerElements)),
            React__default.createElement("tbody", { "aria-live": isLoading !== undefined ? 'polite' : 'off', "aria-busy": isLoading }, fallback || (rowElements.length > 0 ? rowElements : React__default.createElement("tr", null,
                React__default.createElement("td", { colSpan: headerElements.length }, props.children))))),
        compactHeader && React__default.createElement(Menu, { open: isSortMenuOpen, onClose: () => setIsSortMenuOpen(false), anchorEl: sortMenuAnchor.current, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' } }, columns.filter(column => column.sortKey !== undefined).map(column => React__default.createElement(MenuItem, { key: column.id, onClick: () => onHeaderClick(column) }, column.title))));
}
const Row = (({ rowProps, className, onClick, children }) => {
    const { table: strings } = useStrings();
    const ref = useRef(null);
    const classes = classNames(rowProps.className, className, {
        'Table-clickableRow': onClick
    });
    useEffect(() => {
        var _a;
        if (rowProps.isFocused) {
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [rowProps.isFocused]);
    const handleClick = event => {
        var _a;
        (_a = rowProps.onClick) === null || _a === void 0 ? void 0 : _a.call(rowProps, event);
        onClick === null || onClick === void 0 ? void 0 : onClick();
    };
    return React__default.createElement("tr", { ref: ref, className: classes, tabIndex: rowProps.tabIndex, onClick: handleClick, onKeyUp: rowProps.onKeyUp, onKeyDown: rowProps.onKeyDown },
        rowProps.showTick && React__default.createElement("td", { className: "Table-selectCell" },
            React__default.createElement(Checkbox, { color: "primary", value: JSON.stringify(rowProps.id), checked: rowProps.isSelected, title: strings.select, inputProps: {
                    'aria-label': strings.select
                }, onClick: rowProps.onTick })),
        children);
});
Table.Row = Row;
const TextCell = ({ primary, secondary, className, wrap, emptyDash, children }) => {
    const classes = classNames('TableTextCell', className, {
        'TableTextCell-noWrap': wrap === false
    });
    return React__default.createElement("td", { className: classes },
        primary && React__default.createElement("div", { className: "TableTextCell-primary" }, primary),
        secondary && React__default.createElement("div", { className: "TableTextCell-secondary" }, secondary),
        children,
        emptyDash !== false && !primary && !secondary && !children && React__default.createElement("span", { className: "TableTextCell-empty" }, "\u2013"));
};
Table.TextCell = TextCell;
const ActionCell = props => {
    const classes = classNames('TableActionCell', props.className);
    return React__default.createElement("td", { className: classes },
        React__default.createElement("div", { className: "TableActionCell-actions" }, props.children));
};
Table.ActionCell = ActionCell;
/**
 * Helper class for row selection.
 */
class SelectionHelper {
    constructor(rowIds, selectedIds = [], onChange) {
        this.rowIds = rowIds;
        this.onChange = onChange;
        this.selectedIds = new Set(selectedIds);
    }
    select(id, isSelected = true) {
        if (isSelected) {
            this.selectedIds.add(id);
        }
        else {
            this.selectedIds.delete(id);
        }
        return this;
    }
    toggle(id) {
        this.select(id, !this.selectedIds.has(id));
        return this;
    }
    selectOnly(...ids) {
        this.selectedIds.clear();
        ids.forEach(id => this.selectedIds.add(id));
        return this;
    }
    selectAll() {
        this.rowIds.forEach(id => this.selectedIds.add(id));
        return this;
    }
    selectNone() {
        this.selectedIds.clear();
        return this;
    }
    toggleRange(fromIndex, toIndex) {
        const select = !this.selectedIds.has(this.rowIds[toIndex]);
        if (toIndex < fromIndex) {
            const oldFromIndex = fromIndex;
            fromIndex = toIndex;
            toIndex = oldFromIndex;
        }
        for (let i = fromIndex; i <= toIndex; i++) {
            this.select(this.rowIds[i], select);
        }
        return this;
    }
    isSelected(id) {
        return this.selectedIds.has(id);
    }
    fireChange() {
        var _a;
        (_a = this.onChange) === null || _a === void 0 ? void 0 : _a.call(this, Array.from(this.selectedIds));
    }
    getState() {
        if (this.selectedIds.size === 0) {
            return 'none';
        }
        else if (this.rowIds.every(id => this.selectedIds.has(id))) {
            return 'all';
        }
        else {
            return 'some';
        }
    }
}

export { Table as T, TableRowSkeleton as a, faCaretDownExports as f };
